import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText, CT_SKILLS, CT_BG } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Pencil } from "lucide-react";

const emptyQ = { text: "", options: ["", "", "", ""], correct_index: 0, ct_skill: CT_SKILLS[0], active: true };

export default function Content() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [testId, setTestId] = useState("");
  const [newClass, setNewClass] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [subjectClasses, setSubjectClasses] = useState([]);
  const [newChapter, setNewChapter] = useState("");
  const [newTest, setNewTest] = useState("");
  const [qOpen, setQOpen] = useState(false);
  const [qForm, setQForm] = useState(emptyQ);
  const [editQ, setEditQ] = useState(null);

  const loadClasses = async () => setClasses((await api.get("/classes")).data);
  const loadSubjects = async () => setSubjects((await api.get("/subjects")).data);
  useEffect(() => { loadClasses(); loadSubjects(); }, []);

  useEffect(() => {
    if (!classId || !subjectId) { setChapters([]); return; }
    api.get("/chapters", { params: { class_id: classId, subject_id: subjectId } }).then((r) => setChapters(r.data));
    setChapterId(""); setTests([]); setTestId(""); setQuestions([]);
  }, [classId, subjectId]);

  const loadTests = async (cid) => {
    setChapterId(cid); setTestId(""); setQuestions([]);
    setTests((await api.get("/tests", { params: { chapter_id: cid } })).data);
  };
  const loadQuestions = async (tid) => {
    setTestId(tid);
    setQuestions((await api.get("/questions", { params: { test_id: tid } })).data);
  };

  const addClass = async () => {
    if (!newClass.trim()) return;
    await api.post("/classes", { name: newClass.trim(), order: classes.length + 1 });
    setNewClass(""); loadClasses(); toast.success("Class added");
  };
  const addSubject = async () => {
    if (!newSubject.trim() || subjectClasses.length === 0) return toast.error("Enter name and select classes");
    await api.post("/subjects", { name: newSubject.trim(), class_ids: subjectClasses });
    setNewSubject(""); setSubjectClasses([]); loadSubjects(); toast.success("Subject added");
  };
  const addChapter = async () => {
    if (!newChapter.trim() || !classId || !subjectId) return toast.error("Select class & subject first");
    await api.post("/chapters", { class_id: classId, subject_id: subjectId, name: newChapter.trim(), order: chapters.length + 1, active: true });
    setNewChapter("");
    setChapters((await api.get("/chapters", { params: { class_id: classId, subject_id: subjectId } })).data);
  };
  const addTest = async () => {
    if (!newTest.trim() || !chapterId) return toast.error("Select a chapter first");
    await api.post("/tests", { chapter_id: chapterId, title: newTest.trim(), active: true });
    setNewTest(""); loadTests(chapterId);
  };

  const saveQuestion = async (e) => {
    e.preventDefault();
    try {
      const body = { ...qForm, test_id: testId };
      if (editQ) await api.put(`/questions/${editQ}`, body);
      else await api.post("/questions", body);
      setQOpen(false); setQForm(emptyQ); setEditQ(null); loadQuestions(testId);
      toast.success("Question saved");
    } catch (err) { toast.error(errText(err)); }
  };

  const Section = ({ title, children }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h4 className="text-base font-semibold mb-4">{title}</h4>{children}
    </div>
  );

  return (
    <Shell title="Content Management" subtitle="Class → Subject → Chapter → Test → Question" nav={
      <Link to="/admin"><Button variant="ghost" size="sm" data-testid="nav-back-admin"><ArrowLeft className="h-4 w-4 mr-1" />Dashboard</Button></Link>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Classes">
          <div className="flex gap-2">
            <Input placeholder="e.g. Class 5" value={newClass} onChange={(e) => setNewClass(e.target.value)} data-testid="class-name-input" />
            <Button onClick={addClass} data-testid="add-class-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="classes-list">
            {classes.map((c) => (
              <span key={c.id} className="px-3 py-1.5 rounded-md bg-slate-100 text-sm flex items-center gap-2" data-testid={`class-chip-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                {c.name}
                <button onClick={async () => { await api.delete(`/classes/${c.id}`); loadClasses(); }} data-testid={`delete-class-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </button>
              </span>
            ))}
          </div>
        </Section>

        <Section title="Subjects">
          <Input placeholder="e.g. Mathematics" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} data-testid="subject-name-input" />
          <div className="flex flex-wrap gap-3 mt-3">
            {classes.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm" data-testid={`subject-class-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <Checkbox checked={subjectClasses.includes(c.id)}
                  onCheckedChange={(v) => setSubjectClasses(v ? [...subjectClasses, c.id] : subjectClasses.filter((x) => x !== c.id))} />
                {c.name}
              </label>
            ))}
          </div>
          <Button className="mt-4" onClick={addSubject} data-testid="add-subject-btn"><Plus className="h-4 w-4 mr-1" />Add Subject</Button>
          <div className="mt-4 space-y-2" data-testid="subjects-list">
            {subjects.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2" data-testid={`subject-row-${s.name.toLowerCase()}`}>
                <span>{s.name} <span className="text-slate-500">· {s.class_ids.length} classes</span></span>
                <button onClick={async () => { await api.delete(`/subjects/${s.id}`); loadSubjects(); }} data-testid={`delete-subject-${s.name.toLowerCase()}`}>
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </button>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 mt-6">
        <h4 className="text-base font-semibold mb-4">Chapters, Tests & Questions</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="content-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={subjectId} onValueChange={setSubjectId}>
            <SelectTrigger data-testid="content-subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
            <SelectContent>
              {subjects.filter((s) => !classId || s.class_ids.includes(classId)).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {classId && subjectId && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <Label>Chapters</Label>
              <div className="flex gap-2 mt-2">
                <Input placeholder="Chapter name" value={newChapter} onChange={(e) => setNewChapter(e.target.value)} data-testid="chapter-name-input" />
                <Button onClick={addChapter} data-testid="add-chapter-btn"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3 space-y-2" data-testid="chapters-list">
                {chapters.map((c) => (
                  <div key={c.id} className={`flex items-center justify-between px-3 py-2 rounded-md border cursor-pointer text-sm ${chapterId === c.id ? "border-primary bg-accent" : "border-slate-200"}`}
                    onClick={() => loadTests(c.id)} data-testid={`chapter-item-${c.id}`}>
                    <span>{c.name}</span>
                    <button onClick={async (e) => { e.stopPropagation(); await api.delete(`/chapters/${c.id}`); setChapters(chapters.filter((x) => x.id !== c.id)); }} data-testid={`delete-chapter-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Tests</Label>
              <div className="flex gap-2 mt-2">
                <Input placeholder="Test title" value={newTest} onChange={(e) => setNewTest(e.target.value)} data-testid="test-title-input" />
                <Button onClick={addTest} data-testid="add-test-btn"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3 space-y-2" data-testid="tests-list">
                {chapterId && tests.length === 0 && <p className="text-sm text-slate-500">No tests yet</p>}
                {tests.map((t) => (
                  <div key={t.id} className={`flex items-center justify-between px-3 py-2 rounded-md border cursor-pointer text-sm ${testId === t.id ? "border-primary bg-accent" : "border-slate-200"}`}
                    onClick={() => loadQuestions(t.id)} data-testid={`test-item-${t.id}`}>
                    <span>{t.title} <span className="text-slate-500">· {t.question_count} Q</span></span>
                    <button onClick={async (e) => { e.stopPropagation(); await api.delete(`/tests/${t.id}`); loadTests(chapterId); }} data-testid={`delete-test-${t.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Questions</Label>
                {testId && <Button size="sm" onClick={() => { setQForm(emptyQ); setEditQ(null); setQOpen(true); }} data-testid="add-question-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>}
              </div>
              <div className="mt-3 space-y-2" data-testid="questions-list">
                {!testId && <p className="text-sm text-slate-500">Select a test</p>}
                {questions.map((q, i) => (
                  <div key={q.id} className="border border-slate-200 rounded-md p-3" data-testid={`question-item-${q.id}`}>
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-medium">{i + 1}. {q.text}</p>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => { setQForm({ text: q.text, options: [...q.options, "", "", "", ""].slice(0, Math.max(4, q.options.length)), correct_index: q.correct_index, ct_skill: q.ct_skill, active: true }); setEditQ(q.id); setQOpen(true); }} data-testid={`edit-question-${q.id}`}>
                          <Pencil className="h-3.5 w-3.5 text-slate-500" />
                        </button>
                        <button onClick={async () => { await api.delete(`/questions/${q.id}`); loadQuestions(testId); }} data-testid={`delete-question-${q.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-700 mt-1">Ans: {q.options[q.correct_index]}</p>
                    <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full" style={{ background: CT_BG[q.ct_skill] }}>{q.ct_skill}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={qOpen} onOpenChange={setQOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editQ ? "Edit" : "Add"} Question</DialogTitle></DialogHeader>
          <form onSubmit={saveQuestion} className="space-y-4">
            <div><Label>Question text</Label><Input required value={qForm.text} onChange={(e) => setQForm({ ...qForm, text: e.target.value })} data-testid="question-text-input" className="mt-1.5" /></div>
            {qForm.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" checked={qForm.correct_index === i} onChange={() => setQForm({ ...qForm, correct_index: i })} data-testid={`question-correct-${i}`} />
                <Input placeholder={`Option ${i + 1}`} value={o} data-testid={`question-option-${i}`}
                  onChange={(e) => { const opts = [...qForm.options]; opts[i] = e.target.value; setQForm({ ...qForm, options: opts }); }} />
              </div>
            ))}
            <div>
              <Label>Primary CT Skill</Label>
              <Select value={qForm.ct_skill} onValueChange={(v) => setQForm({ ...qForm, ct_skill: v })}>
                <SelectTrigger className="mt-1.5" data-testid="question-ct-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CT_SKILLS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full" data-testid="save-question-btn">Save Question</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
